import React from 'react';
import { X, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import PageThumbnailPreview from './PageThumbnailPreview';

// The All Pages grid renders each page with the shared live PageThumbnailPreview
// — the same renderer used by the editor's page strip and template galleries.
// It already paints text/SVG at full size on its own GPU layer and downscales
// the layer like an image (supersampling), so thumbnails match the editor
// without re-rasterizing glyphs at the tiny size. It also renders directly from
// the live `pages` prop, so nothing can go stale.

const GridViewModal = ({ pages, currentPageId, canvasSize, onSwitchPage, onClose }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={onClose}>
    <div
      className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white rounded-t-2xl">
        <h2 className="text-base font-semibold">All Pages ({pages.length})</h2>
        <Button size="icon" variant="ghost" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="overflow-auto p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
          {pages.map((page, index) => {
            const isActive = page.id === currentPageId;
            return (
              <div
                key={page.id}
                onClick={() => onSwitchPage(page.id)}
                className={`cursor-pointer group rounded-xl overflow-hidden border-2 transition-all shadow-sm hover:shadow-lg ${
                  isActive ? 'border-purple-500 shadow-purple-100' : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="relative w-full bg-white">
                  <PageThumbnailPreview page={page} canvasSize={canvasSize} thumbWidth={240} fillWidth={true} />
                  {isActive && (
                    <div className="absolute top-2 left-2 bg-purple-600 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                      Editing
                    </div>
                  )}
                  {page.locked && (
                    <div className="absolute top-2 right-2 bg-black/50 rounded p-1">
                      <Lock className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div className={`px-3 py-2 ${isActive ? 'bg-purple-50' : 'bg-white'}`}>
                  <p className="text-xs font-medium truncate text-gray-800">{page.name}</p>
                  {/* Item count lives in the footer — never overlaid on the
                      thumbnail, where it would sit on top of design content
                      (e.g. a bottom-anchored address line) and read as garbled text. */}
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Page {index + 1}
                    {(page.elements || []).length > 0 ? ` · ${(page.elements || []).length} items` : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </div>
);

export default GridViewModal;
